import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import Logo from '../../../components/Logo/Logo';
import Toast from '../../../components/Toast/Toast';
import authService from '../../../services/api/authService';
import { getPasswordErrors } from '../../../utils/validation';

type VerifyState = 'verifying' | 'valid' | 'invalid' | 'already-done';

export default function ResetPasswordConfirm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  const [verifyState, setVerifyState] = useState<VerifyState>('verifying');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);

  const passwordErrors = getPasswordErrors(newPassword);

  useEffect(() => {
    // Firebase's hosted reset-password page performs the reset itself and
    // then sends the user back to our continueUrl with no oobCode attached
    // (the code was already consumed there). That's a success case, not an
    // error, so it's handled separately from a genuinely invalid/expired link.
    if (!oobCode) {
      setVerifyState(mode ? 'invalid' : 'already-done');
      return;
    }

    if (mode !== 'resetPassword') {
      setVerifyState('invalid');
      return;
    }

    authService
      .verifyResetCode(oobCode)
      .then((verifiedEmail) => {
        setEmail(verifiedEmail);
        setVerifyState('valid');
      })
      .catch(() => {
        setVerifyState('invalid');
      });
  }, [mode, oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!oobCode) return;

    if (newPassword !== confirmPassword) {
      setToast({ message: 'Passwords do not match.', type: 'error' });
      return;
    }
    if (passwordErrors.length > 0) {
      setToast({ message: 'Please meet all password requirements.', type: 'error' });
      return;
    }

    setIsSubmitting(true);
    try {
      await authService.resetPassword(oobCode, newPassword);
      setIsDone(true);
      setToast({ message: 'Password reset successfully. Please sign in.', type: 'success' });
      setTimeout(() => navigate('/', { replace: true }), 2000);
    } catch (error: any) {
      let errorMessage = 'Something went wrong. Please request a new reset link.';
      if (error?.code === 'auth/expired-action-code') {
        errorMessage = 'This reset link has expired. Please request a new one.';
      } else if (error?.code === 'auth/invalid-action-code') {
        errorMessage = 'This reset link is invalid or has already been used.';
      } else if (error?.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please use a stronger password.';
      } else if (error?.message) {
        errorMessage = error.message;
      }
      setToast({ message: errorMessage, type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden lg:flex lg:w-5/12 xl:w-1/2 flex-col items-center justify-center bg-gradient-to-br from-brand-primary to-brand-gray p-12 text-white">
        <div className="max-w-xs text-center">
          <div className="mb-3 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20">
            <svg viewBox="0 0 24 24" className="h-8 w-8 fill-white" aria-hidden="true">
              <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8Z" />
              <path d="M12 6a1 1 0 0 0-1 1v5.59l-3.71 3.7a1 1 0 1 0 1.42 1.42l4-4A1 1 0 0 0 13 13V7a1 1 0 0 0-1-1Z" />
            </svg>
          </div>
          <h2 className="mb-3 text-3xl font-bold">Reset Password</h2>
          <p className="text-base leading-relaxed text-white/75">
            Choose a new password to regain access to your account.
          </p>
        </div>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center bg-gray-50 px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex justify-center">
            <div className="w-36 sm:w-44">
              <Logo />
            </div>
          </div>

          {verifyState === 'verifying' && (
            <p className="text-center text-sm text-gray-600">Verifying your reset link…</p>
          )}

          {verifyState === 'invalid' && (
            <>
              <h1 className="mb-1 text-2xl font-bold text-gray-950">Link expired or invalid</h1>
              <p className="mb-8 text-sm text-gray-600">
                This password reset link is no longer valid. Please request a new one.
              </p>
              <Button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="inline-flex w-full items-center justify-center rounded-lg bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-hover"
              >
                Request a new link
              </Button>
            </>
          )}

          {verifyState === 'already-done' && (
            <>
              <h1 className="mb-1 text-2xl font-bold text-gray-950">Password reset</h1>
              <p className="mb-8 text-sm text-gray-600">
                Your password has been changed successfully. You can now sign in with your new password.
              </p>
              <Button
                type="button"
                onClick={() => navigate('/', { replace: true })}
                className="inline-flex w-full items-center justify-center rounded-lg bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-hover"
              >
                Go to Sign In
              </Button>
            </>
          )}

          {verifyState === 'valid' && !isDone && (
            <>
              <h1 className="mb-1 text-2xl font-bold text-gray-950">Set a new password</h1>
              <p className="mb-8 text-sm text-gray-600">
                Resetting password for <span className="font-semibold">{email}</span>
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="new-password" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                    New Password
                  </label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showNew ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-10 text-sm outline-none transition focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowNew(!showNew)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600"
                      aria-label={showNew ? 'Hide password' : 'Show password'}
                    >
                      {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="confirm-password" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showConfirm ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-10 text-sm outline-none transition focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600"
                      aria-label={showConfirm ? 'Hide password' : 'Show password'}
                    >
                      {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <p className="mb-2 text-xs font-medium text-gray-500">Password must contain:</p>
                  <ul className="space-y-1 text-xs">
                    {[
                      { label: 'At least 10 characters', met: newPassword.length >= 10 },
                      { label: 'One lowercase letter', met: /[a-z]/.test(newPassword) },
                      { label: 'One uppercase letter', met: /[A-Z]/.test(newPassword) },
                      { label: 'One number', met: /\d/.test(newPassword) },
                      { label: 'One special character (#@$!%*?&-)', met: /[#@$!%*?&-]/.test(newPassword) },
                    ].map(({ label, met }) => (
                      <li key={label} className={`flex items-center gap-2 ${met ? 'text-green-600' : 'text-brand-primary'}`}>
                        <span>{met ? '✓' : '✗'}</span>
                        {label}
                      </li>
                    ))}
                  </ul>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting || !newPassword || !confirmPassword}
                  className="inline-flex w-full items-center justify-center rounded-lg bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-hover disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  {isSubmitting ? 'Saving...' : 'Save New Password'}
                </Button>
              </form>
            </>
          )}

          {isDone && (
            <>
              <h1 className="mb-1 text-2xl font-bold text-gray-950">Password reset</h1>
              <p className="mb-8 text-sm text-gray-600">
                Your password has been updated. Redirecting you to sign in…
              </p>
            </>
          )}
        </div>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
